import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import Button from 'views/Button.js';
import Product from 'classes/Product.js';
import { getSpecs } from 'files/ProductSpecs.js';
import { waterSensorPerUnit, maxTierQty } from 'files/SensorPricing.js';
import { applyOffer, offerPricing } from 'files/LimitedOffer.js';
import PriceGate from 'views/PriceGate.js';
import ShopPayInstallments from 'views/ShopPayInstallments.js';
import OfferTag from 'views/OfferTag.js';
import { useServiceability } from 'views/ServiceabilityProvider.js';

const ProductDetail = ({ handle, products, onAddToCart, loading }) => {

    const history = useHistory();
    const { offer, volumePricing } = useServiceability();

    const product = (products || []).find(p => p.handle === handle) || Product.getByHandle(handle);
    const specs = getSpecs(handle);
    const images = (product && product.images) || [];
    const [imgIndex, setImgIndex] = useState(0);

    // Route change — start at the top and reset the gallery
    useEffect(() => {
        window.scrollTo(0, 0);
        setImgIndex(0);
    }, [handle]);

    // Auto-advance the gallery when there's more than one image
    useEffect(() => {
        if(images.length <= 1) {
            return;
        }
        const timer = setInterval(() => {
            setImgIndex(i => (i + 1) % images.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [images.length, handle]);

    // Loading skeleton while live product data is still being fetched
    if(loading) {
        return (
            <div style={{ paddingTop: Appearance.layout.headerOffset() }}>
                <section style={{ backgroundColor: Appearance.colors.white }}>
                    <div style={{ maxWidth: Appearance.layout.maxWidth, margin: '0 auto', padding: Utils.isMobile() ? '28px 20px 64px' : '36px 40px 96px' }}>
                        <div className={'skeleton'} style={{ height: 14, width: 120, marginBottom: 28 }} />
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: Utils.isMobile() ? '1fr' : '1fr 1fr',
                            gap: Utils.isMobile() ? 36 : 72,
                            alignItems: 'center',
                        }}>
                            <div className={'skeleton'} style={{ height: Utils.isMobile() ? 320 : 480, borderRadius: 28 }} />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div className={'skeleton'} style={{ height: 14, width: '35%' }} />
                                <div className={'skeleton'} style={{ height: 44, width: '65%' }} />
                                <div className={'skeleton'} style={{ height: 16, width: '95%' }} />
                                <div className={'skeleton'} style={{ height: 16, width: '80%' }} />
                                <div className={'skeleton'} style={{ height: 30, width: 120, marginTop: 12 }} />
                                <div className={'skeleton'} style={{ height: 50, width: 190, borderRadius: 25, marginTop: 8 }} />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if(!product) {
        return (
            <section style={{ maxWidth: 680, margin: '0 auto', padding: '160px 20px', textAlign: 'center' }}>
                <h1 style={Appearance.textStyles.heading()}>{'Product not found'}</h1>
                <div style={{ marginTop: 24 }}>
                    <Button label={'Back to shop'} type={'primary'} onClick={() => history.push('/')} />
                </div>
            </section>
        );
    }

    const backLink = (
        <span
            onClick={() => history.push('/')}
            style={{
                color: Appearance.colors.primary(),
                fontFamily: Appearance.fonts.body,
                fontSize: 15,
                fontWeight: 500,
                cursor: 'pointer',
            }}>
            {'‹ All products'}
        </span>
    );

    const imageStage = (
        <div>
            {/* Gallery stage — images crossfade and auto-advance */}
            <div style={{
                position: 'relative',
                borderRadius: 28,
                backgroundColor: Appearance.colors.offWhite,
                height: Utils.isMobile() ? 320 : 480,
                overflow: 'hidden',
            }}>
                {images.length > 0 ? images.map((img, i) => (
                    <img
                        key={img.id || i}
                        src={img.src}
                        alt={img.altText || product.title}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: i === imgIndex ? 1 : 0,
                            transition: 'opacity 0.6s ease',
                        }} />
                )) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className={product.icon || 'fas fa-box'} style={{ fontSize: 110, color: Appearance.colors.lightGrey }} />
                    </div>
                )}
            </div>

            {/* Dots */}
            {images.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 18 }}>
                    {images.map((img, i) => (
                        <button
                            key={img.id || i}
                            onClick={() => setImgIndex(i)}
                            aria-label={`View image ${i + 1}`}
                            style={{
                                width: i === imgIndex ? 22 : 8,
                                height: 8,
                                borderRadius: 4,
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                backgroundColor: i === imgIndex ? Appearance.colors.primary() : Appearance.colors.border(),
                                transition: 'all 0.3s ease',
                            }} />
                    ))}
                </div>
            )}
        </div>
    );

    const pricing = offerPricing(product, offer);

    const buyPanel = (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <span style={{ ...Appearance.textStyles.label({ color: Appearance.colors.primary() }), marginBottom: 14, display: 'block' }}>
                {(specs && specs.tagline) || product.subtitle}
            </span>
            <h1 style={{ ...Appearance.textStyles.heading({ fontSize: Utils.isMobile() ? 32 : 46 }), marginBottom: 18 }}>
                {product.title}
            </h1>
            <p style={{ ...Appearance.textStyles.body({ fontSize: Utils.isMobile() ? 16 : 18, lineHeight: 1.5 }), maxWidth: '42ch', marginBottom: 28 }}>
                {product.description}
            </p>

            <PriceGate block={false} buttonStyle={{ padding: '15px 40px', fontSize: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={Appearance.textStyles.heading({ fontSize: 28 })}>
                        {Utils.formatCurrency(pricing.price)}
                    </span>
                    {pricing.was && (
                        <span style={{ ...Appearance.textStyles.body({ fontSize: 17 }), textDecoration: 'line-through', color: Appearance.colors.lightGrey }}>
                            {Utils.formatCurrency(pricing.was)}
                        </span>
                    )}
                    <OfferTag percent={pricing.percent} style={{ alignSelf: 'center' }} />
                </div>

                {/* Spacing lives on the button, not the installments line —
                    that line renders nothing under $50 (e.g. a $49 sensor). */}
                <ShopPayInstallments price={pricing.price} />

                <div style={{ marginTop: 36 }}>
                    <Button label={'Add to Cart'} type={'primary'} onClick={() => onAddToCart && onAddToCart(product, 'product_detail')} style={{ padding: '15px 40px', fontSize: 16 }} />
                </div>
            </PriceGate>

            {/* Volume pricing — a light tagline; the discount auto-applies in cart */}
            {volumePricing && product.tiers && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginTop: 24 }}>
                    <i className={'fas fa-tags'} style={{ fontSize: 12, color: Appearance.colors.primary() }} />
                    <span style={Appearance.textStyles.caption({ color: Appearance.colors.subText() })}>
                        {`Covering more than one area? Volume pricing drops to ${Utils.formatCurrency(applyOffer(waterSensorPerUnit(product.tiers, maxTierQty(product.tiers)), pricing.percent))}/ea — applied automatically in your cart.`}
                    </span>
                </div>
            )}

            <div style={{ display: 'flex', gap: 22, marginTop: 24, flexWrap: 'wrap' }}>
                {['Free shipping', 'No monthly fees', '14-day returns'].map((item, index) => (
                    <span key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <i className={'fas fa-check'} style={{ fontSize: 12, color: Appearance.colors.primary() }} />
                        <span style={Appearance.textStyles.caption({ color: Appearance.colors.subText() })}>{item}</span>
                    </span>
                ))}
            </div>
        </div>
    );

    return (
        <div style={{ paddingTop: Appearance.layout.headerOffset() }}>

            {/* Hero */}
            <section style={{ backgroundColor: Appearance.colors.white }}>
                <div style={{ maxWidth: Appearance.layout.maxWidth, margin: '0 auto', padding: Utils.isMobile() ? '28px 20px 64px' : '36px 40px 96px' }}>
                    <div style={{ marginBottom: 28 }}>{backLink}</div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: Utils.isMobile() ? '1fr' : '1fr 1fr',
                        gap: Utils.isMobile() ? 36 : 72,
                        alignItems: 'center',
                    }}>
                        {imageStage}
                        {buyPanel}
                    </div>
                </div>
            </section>

            {/* Highlights */}
            {specs && specs.highlights && (
                <section style={{ backgroundColor: Appearance.colors.offWhite }}>
                    <div style={{ maxWidth: Appearance.layout.maxWidth, margin: '0 auto', padding: Appearance.layout.sectionPadding() }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: Utils.isMobile() ? '1fr' : 'repeat(3, 1fr)',
                            gap: Utils.isMobile() ? 20 : 24,
                        }}>
                            {specs.highlights.map((h, index) => (
                                <div key={index} style={{
                                    ...Appearance.styles.card(),
                                    boxShadow: Appearance.boxShadow.card,
                                    padding: Utils.isMobile() ? 24 : 28,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                        <i className={h.icon} style={{ fontSize: 19, color: Appearance.colors.primary(), flexShrink: 0 }} />
                                        <h3 style={{ ...Appearance.textStyles.subHeading({ fontSize: 18 }), margin: 0 }}>{h.title}</h3>
                                    </div>
                                    <p style={Appearance.textStyles.body({ fontSize: 15 })}>{h.copy}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Tech specs */}
            {specs && specs.specs && (
                <section style={{ backgroundColor: Appearance.colors.white }}>
                    <div style={{ maxWidth: Appearance.layout.readableWidth, margin: '0 auto', padding: Appearance.layout.sectionPadding() }}>
                        <h2 style={{ ...Appearance.textStyles.sectionHeading({ textAlign: 'left' }), marginBottom: 28 }}>
                            {'Tech specs'}
                        </h2>
                        <div style={{ border: `1px solid ${Appearance.colors.border()}`, borderRadius: 16, overflow: 'hidden' }}>
                            {specs.specs.map((row, index) => (
                                <div key={index} style={{
                                    display: 'flex',
                                    gap: 20,
                                    padding: Utils.isMobile() ? '14px 16px' : '16px 22px',
                                    borderBottom: index < specs.specs.length - 1 ? `1px solid ${Appearance.colors.divider()}` : 'none',
                                }}>
                                    <span style={{ ...Appearance.textStyles.body({ fontSize: 14, color: Appearance.colors.subText() }), width: '38%', flexShrink: 0 }}>
                                        {row.label}
                                    </span>
                                    <span style={Appearance.textStyles.body({ fontSize: 15, color: Appearance.colors.text(), fontWeight: 500 })}>
                                        {row.value}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* In the box */}
                        {specs.inTheBox && (
                            <div style={{ marginTop: 40 }}>
                                <h3 style={{ ...Appearance.textStyles.subHeading(), marginBottom: 16 }}>{'In the box'}</h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                    {specs.inTheBox.map((item, index) => (
                                        <span key={index} style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 8,
                                            padding: '10px 16px', borderRadius: 999,
                                            border: `1px solid ${Appearance.colors.border()}`,
                                        }}>
                                            <i className={'fas fa-check'} style={{ fontSize: 11, color: Appearance.colors.primary() }} />
                                            <span style={Appearance.textStyles.body({ fontSize: 14, color: Appearance.colors.text() })}>{item}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Closing CTA */}
            <section style={{ backgroundColor: Appearance.colors.offWhite }}>
                <div style={{ maxWidth: Appearance.layout.maxWidth, margin: '0 auto', padding: Appearance.layout.sectionPadding(), textAlign: 'center' }}>
                    <h2 style={{ ...Appearance.textStyles.sectionHeading(), marginBottom: 14 }}>
                        {`Add the ${product.shortTitle || product.title} to your network`}
                    </h2>
                    <p style={{ ...Appearance.textStyles.sectionSubheading(), margin: '0 auto 28px' }}>
                        {'No subscription, no monthly fees. Yours to keep.'}
                    </p>
                    <PriceGate block={false} buttonStyle={{ padding: '15px 40px', fontSize: 16 }}>
                        <Button label={`Add to Cart · ${Utils.formatCurrency(pricing.price)}`} type={'primary'} onClick={() => onAddToCart && onAddToCart(product, 'product_detail')} style={{ padding: '15px 40px', fontSize: 16 }} />
                        <ShopPayInstallments price={pricing.price} align={'center'} style={{ marginTop: 16 }} />
                    </PriceGate>
                </div>
            </section>
        </div>
    );
};

export default ProductDetail;
